import { useMemo, useState } from 'react';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import { Link } from 'react-router-dom';
import {
  withLoading,
  useTranslator,
  CategoryMealTag,
  CookbookMealListEdge,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button } from 'reactstrap';
import { Layout } from '../../Layout';
import TitleBar from '../../Layout/TitleBar';
import FilterOptionGroup, { FilterOption } from './FilterOptionGroup';

const uniqueTagsById = (tags: CategoryMealTag[]): CategoryMealTag[] =>
  Array.from(new Map(tags.map((tag) => [tag.id, tag])).values());

/**
 * Exclusive upper bounds, in minutes, of the preparation-time buckets. Each option covers everything
 * from the previous bound up to its own, so a meal always lands in exactly one of them: 1 means no
 * preparation time at all, 5 means under five minutes, and Infinity means over an hour.
 */
const PREPARATION_TIME_BOUNDS = [1, 5, 15, 30, 45, 60, Infinity];

const KCAL_RANGES = [150, 200, 300, 400, 500, 600];

/** How far a meal's kcal may sit from the picked value and still match. */
const KCAL_TOLERANCE = 50;

const KCAL_OPTIONS: FilterOption[] = KCAL_RANGES.map((range) => ({
  id: range,
  name: `${range} kcal`,
}));

const FILTER_GROUPS = [
  'mealTypeTags',
  'dietTypeTags',
  'customTags',
  'kcal',
  'preparationTime',
] as const;

type FilterGroup = (typeof FILTER_GROUPS)[number];

type ActiveFilterIds = Record<FilterGroup, number[]>;

const EMPTY_ACTIVE_FILTER_IDS: ActiveFilterIds = {
  mealTypeTags: [],
  dietTypeTags: [],
  customTags: [],
  kcal: [],
  preparationTime: [],
};

/**
 * Ids of the options a meal matches within each group. Tag groups yield the meal's own tag ids;
 * kcal yields every value the meal lands within KCAL_TOLERANCE of, so a single meal can match more
 * than one; preparation time yields exactly one bucket. Typing this as a full Record means adding a
 * group to FILTER_GROUPS without teaching it how to match is a compile error.
 */
const OPTION_ID_EXTRACTORS: Record<FilterGroup, (meal: CookbookMealListEdge) => number[]> = {
  mealTypeTags: (meal) => meal.node.tags.mealTypeTags.map((tag) => tag.id),
  dietTypeTags: (meal) => meal.node.tags.dietTypeTags.map((tag) => tag.id),
  customTags: (meal) => meal.node.tags.customTags.map((tag) => tag.id),
  kcal: (meal) =>
    KCAL_RANGES.filter(
      (range) => Math.abs(meal.node.nutrition.singleServing.kcal - range) <= KCAL_TOLERANCE
    ),
  preparationTime: (meal) => {
    const bucket = PREPARATION_TIME_BOUNDS.find(
      (bound) => meal.node.preparationTime.minutes < bound
    );

    return bucket === undefined ? [] : [bucket];
  },
};

const matchedOptionIds = (meal: CookbookMealListEdge, group: FilterGroup): number[] =>
  OPTION_ID_EXTRACTORS[group](meal);

/** OR within a group: a group with nothing selected adds no constraint. */
const matchesFilterGroup = (
  meal: CookbookMealListEdge,
  group: FilterGroup,
  activeInGroup: number[]
) =>
  activeInGroup.length === 0 ||
  matchedOptionIds(meal, group).some((optionId) => activeInGroup.includes(optionId));

/**
 * Option ids still worth showing in `group` — collected from meals that satisfy every OTHER group.
 * The group's own selection is ignored on purpose: options inside a group are OR-ed, so picking
 * another one only widens the result and must never disable its siblings.
 */
const selectableOptionIdsInGroup = (
  meals: CookbookMealListEdge[],
  group: FilterGroup,
  activeFilterIds: ActiveFilterIds
) =>
  new Set(
    meals
      .filter((meal) =>
        FILTER_GROUPS.every(
          (otherGroup) =>
            otherGroup === group ||
            matchesFilterGroup(meal, otherGroup, activeFilterIds[otherGroup])
        )
      )
      .flatMap((meal) => matchedOptionIds(meal, group))
  );

const CookbookFilterContainer = ({ meals }: { meals: CookbookMealListEdge[] }) => {
  const { t } = useTranslator();

  const { mealTypeTags, customTags, dietTypeTags } = useMemo(
    () => ({
      mealTypeTags: uniqueTagsById(meals.flatMap((meal) => meal.node.tags.mealTypeTags)),
      customTags: uniqueTagsById(meals.flatMap((meal) => meal.node.tags.customTags)),
      dietTypeTags: uniqueTagsById(meals.flatMap((meal) => meal.node.tags.dietTypeTags)),
    }),
    [meals]
  );

  const preparationTimeOptions = useMemo<FilterOption[]>(
    () =>
      PREPARATION_TIME_BOUNDS.map((bound, index) => {
        if (index === 0) {
          return { id: bound, name: t('cookbook.filters.preparationTime.none') };
        }

        if (bound === Infinity) {
          return { id: bound, name: `> ${PREPARATION_TIME_BOUNDS[index - 1]} min` };
        }

        return { id: bound, name: `< ${bound} min` };
      }),
    [t]
  );

  const [activeFilterIds, setActiveFilterIds] = useState<ActiveFilterIds>(EMPTY_ACTIVE_FILTER_IDS);

  const handleOptionClick = (group: FilterGroup, optionId: number) => {
    setActiveFilterIds((prevActiveFilterIds) => {
      const activeInGroup = prevActiveFilterIds[group];

      return {
        ...prevActiveFilterIds,
        [group]: activeInGroup.includes(optionId)
          ? activeInGroup.filter((id) => id !== optionId)
          : [...activeInGroup, optionId],
      };
    });
  };

  // AND between groups (every), OR within a group (matchesFilterGroup).
  const filteredMeals = useMemo(
    () =>
      meals.filter((meal) =>
        FILTER_GROUPS.every((group) => matchesFilterGroup(meal, group, activeFilterIds[group]))
      ),
    [meals, activeFilterIds]
  );

  // An option is disabled when no meal matches it once the other groups' filters are applied.
  const disabledFilterIds = useMemo<ActiveFilterIds>(() => {
    const disabledIdsIn = (group: FilterGroup, options: FilterOption[]) => {
      const selectableIds = selectableOptionIdsInGroup(meals, group, activeFilterIds);

      return options.filter((option) => !selectableIds.has(option.id)).map((option) => option.id);
    };

    return {
      mealTypeTags: disabledIdsIn('mealTypeTags', mealTypeTags),
      dietTypeTags: disabledIdsIn('dietTypeTags', dietTypeTags),
      customTags: disabledIdsIn('customTags', customTags),
      kcal: disabledIdsIn('kcal', KCAL_OPTIONS),
      preparationTime: disabledIdsIn('preparationTime', preparationTimeOptions),
    };
  }, [meals, activeFilterIds, mealTypeTags, dietTypeTags, customTags, preparationTimeOptions]);

  return (
    <Layout>
      <TitleBar level={1}>
        {t('cookbook.filters.navbarTitle')}
        <div className="title-bar-icons">
          <Button
            color="link"
            className="p-0"
            onClick={() => setActiveFilterIds(EMPTY_ACTIVE_FILTER_IDS)}
          >
            {t('cookbook.filters.clearButton.navbar')}
          </Button>
        </div>
      </TitleBar>
      <div className="page-template-cookbook">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="cookbook-container">
                  <FilterOptionGroup
                    title={t('cookbook.filters.preparationTime.title')}
                    options={preparationTimeOptions}
                    activeIds={activeFilterIds.preparationTime}
                    disabledIds={disabledFilterIds.preparationTime}
                    columns={3}
                    onToggle={(optionId) => handleOptionClick('preparationTime', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.mealType.title')}
                    options={mealTypeTags}
                    activeIds={activeFilterIds.mealTypeTags}
                    disabledIds={disabledFilterIds.mealTypeTags}
                    onToggle={(optionId) => handleOptionClick('mealTypeTags', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.dietType.title')}
                    options={dietTypeTags}
                    activeIds={activeFilterIds.dietTypeTags}
                    disabledIds={disabledFilterIds.dietTypeTags}
                    onToggle={(optionId) => handleOptionClick('dietTypeTags', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.calories.title')}
                    options={KCAL_OPTIONS}
                    activeIds={activeFilterIds.kcal}
                    disabledIds={disabledFilterIds.kcal}
                    columns={3}
                    onToggle={(optionId) => handleOptionClick('kcal', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.customTag.title')}
                    options={customTags}
                    activeIds={activeFilterIds.customTags}
                    disabledIds={disabledFilterIds.customTags}
                    onToggle={(optionId) => handleOptionClick('customTags', optionId)}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <div className="sticky-footer">
        <Container className="text-center">
          <Row>
            <Col md={{ size: 4, offset: 4 }}>
              <Button
                color="primary"
                className="w-100"
                tag={Link}
                to="/meals/cookbook/filtered-meals"
                state={{
                  meals: filteredMeals,
                }}
              >
                {t('cookbook.filters.buttonShowResult', { count: filteredMeals.length })}
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="" />;

export default withLoading(CookbookFilterContainer, Placeholder);
